import React, { useMemo, useCallback } from 'react'
import { Target, Factory } from 'lucide-react'
import { AnimatedTabContent } from '@/components/ui/animated-tab-content'
import { useAdminInitiatives } from '@/features/project-initiatives/hooks'
import { useIndustries } from '@/features/industries/hooks'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import ProjectInitiativesManagement from './project-initiatives-management'
import { SettingsIndustriesPage } from '@/pages/settings/settings-industries-page'
import { useAuthorization } from '@/features/authorization/AuthorizationContext'
import { useAuthentication } from '@/features/auth/AuthenticationContext'

const RecommendationEngineManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { actions: userActions } = useAuthorization()
  const { user } = useAuthentication()
  const isAdmin = user?.is_administrator === true

  const canView = (action: string) => isAdmin || userActions.has(action)

  const tabs = [
    { id: 'project-initiatives' as const, label: 'Project Initiatives', icon: Target, requiredAction: 'manage_initiatives' },
    { id: 'industry-benchmarks' as const, label: 'Industry Benchmarks', icon: Factory, requiredAction: 'manage_industries' },
  ]

  // Filter tabs to only those the user can access
  const availableTabs = tabs.filter(tab => canView(tab.requiredAction))

  const validTabs = availableTabs.map(t => t.id)
  type TabId = typeof tabs[number]['id']
  const tabParam = searchParams.get('tab') as TabId | null
  const activeTab: TabId = tabParam && validTabs.includes(tabParam as any) ? tabParam : (validTabs[0] ?? 'project-initiatives')
  const setActiveTab = useCallback((tab: TabId) => {
    setSearchParams({ tab }, { replace: true })
  }, [setSearchParams])

  // Fetch data for counts
  const { data: initiativesData } = useAdminInitiatives()
  const { data: industries } = useIndustries()

  // Derive counts
  const initiativeCount = useMemo(() => {
    // Handle paginated response: { items: [], total: X, ... }
    if (initiativesData?.total != null) return initiativesData.total
    // Handle legacy or nested data: { data: { initiatives: [] } }
    const initiatives = initiativesData?.items || initiativesData?.data?.initiatives || initiativesData?.initiatives || []
    return Array.isArray(initiatives) ? initiatives.length : 0
  }, [initiativesData])

  const industryCount = useMemo(() => {
    return Array.isArray(industries) ? industries.length : 0
  }, [industries])

  const countMap: Record<string, number> = {
    'project-initiatives': initiativeCount,
    'industry-benchmarks': industryCount,
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <h1 className="text-2xl font-bold text-gray-900">Recommendation Engine</h1>
        <p className="text-gray-600">Manage project initiatives and industry benchmarks for maturity recommendations</p>
      </motion.div>

      {/* Tab Navigation — underline style matching General page */}
      <motion.div
        className="border-b border-gray-200"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <nav className="-mb-px flex space-x-8">
          {availableTabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon className="h-4 w-4 inline mr-2" />
                {tab.label}
                <span className="ml-1.5 text-xs font-normal rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">
                  {countMap[tab.id] ?? 0}
                </span>
              </button>
            )
          })}
        </nav>
      </motion.div>

      {/* Tab Content */}
      <AnimatedTabContent activeTab={activeTab}>
        {activeTab === 'project-initiatives' && <ProjectInitiativesManagement />}
        {activeTab === 'industry-benchmarks' && <SettingsIndustriesPage />}
      </AnimatedTabContent>
    </motion.div>
  )
}

export default RecommendationEngineManagement
